import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { HiChevronDown, HiChevronRight } from 'react-icons/hi'
import Header from '../components/Header'

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set())

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems)
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index)
    } else {
      newOpenItems.add(index)
    }
    setOpenItems(newOpenItems)
  }

  const faqs = [
    {
      question: "What is CitySeeCityFix?",
      answer: "CitySeeCityFix is a mobile app that makes it incredibly easy to report city issues like potholes, broken streetlights, or damaged signs. Simply take a photo and the app handles the rest - analyzing, describing, and routing the issue to the right city department."
    },
    {
      question: "How does CitySeeCityFix work?",
      answer: "It's simple! Open the app, snap a photo of the problem, and put your phone away. CitySeeCityFix uses AI to identify the issue, creates a detailed report with location data, and automatically sends it to the correct city department. We call it 'Click & Pocket' - one tap is all it takes."
    },
    {
      question: "What types of issues can I report?",
      answer: "You can report various city infrastructure issues including potholes, broken streetlights, damaged signs, graffiti, sidewalk damage, street flooding, and other public safety concerns. The app automatically categorizes and prioritizes issues based on safety and accessibility."
    },
    {
      question: "Do I need to create an account?",
      answer: "While you can report issues anonymously, creating an account allows you to track your submissions, earn civic points, and see the status of your reports when your city is participating in the program."
    },
    {
      question: "How do civic points work?",
      answer: "Civic points reward active community participation: 2 points for being the first to flag an issue, 1 point for confirming existing problems, and 5 points for each neighbor you invite who joins. Top contributors can earn city-branded rewards and recognition."
    },
    {
      question: "Can I track the progress of my reports?",
      answer: "Yes! When your city partners with CitySeeCityFix, you can follow your submissions in real-time. See when they're received, assigned to city crews, and ultimately resolved. This transparency builds trust and shows the impact of your civic engagement."
    },
    {
      question: "Is my personal information shared with the city?",
      answer: "Your privacy is important. While your contact information may be shared with city staff for follow-up questions, your personal details are never publicly displayed. The focus is on the issue and location, not the reporter."
    },
    {
      question: "What if I report something that's already been reported?",
      answer: "No problem! CitySeeCityFix's AI can detect duplicate reports and will let you know if someone else has already flagged the same issue. You'll still earn points for confirming existing problems, helping cities prioritize repairs."
    },
    {
      question: "How does CitySeeCityFix help cities?",
      answer: "CitySeeCityFix provides cities with smart routing, seamless integration with existing systems, custom branding options, GIS-powered reports, and improved community engagement. It turns complaints into collaboration and helps city staff work more efficiently."
    },
    {
      question: "What cities are currently using CitySeeCityFix?",
      answer: "CitySeeCityFix is actively partnering with cities across the country. Contact us to learn if your city is participating or to express interest in bringing CitySeeCityFix to your community. We're always looking to expand our network of civic-minded cities."
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 py-16 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Everything you need to know about CitySeeCityFix and how it's making cities better, one report at a time.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  onClick={() => toggleItem(index)}
                >
                  <span className="text-lg font-medium text-gray-900 dark:text-white">
                    {faq.question}
                  </span>
                  {openItems.has(index) ? (
                    <HiChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <HiChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {openItems.has(index) && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Still have questions?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            Can't find what you're looking for? We're here to help!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="btn-primary"
            >
              Back to Home
            </Link>
            <a
              href="mailto:support@CitySeeCityFix.com"
              className="btn-secondary"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 dark:bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  className="h-8 w-8 rounded-full"
                  src="/maggie_round.png"
                  alt="CitySeeCityFix"
                />
                <span className="ml-3 text-xl font-bold text-white">
                  CitySeeCityFix
                </span>
              </div>
              <p className="text-gray-400">
                The easiest way to report city issues—just one click away.
              </p>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Residents</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li><Link to="/maggie" className="hover:text-white">Meet Maggie</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Cities</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/cities" className="hover:text-white">Learn More</Link></li>
                <li><Link to="/login" className="hover:text-white">Admin Login</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Connect</h3>
              <p className="text-gray-400">
                <a href="https://api.cityseecityfix.com" className="hover:text-white">
                  API Documentation
                </a>
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CitySeeCityFix. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default FAQ
